import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { PanelLeft } from 'lucide-react';
import {
    type ReactNode,
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import {
    PANEL_WINDOW_INSET_PX,
    type PanelPosition,
} from '~/components/toolbar/shared/panelPlacement';
import PanelFrame from '~/components/toolbar/shared/PanelFrame';
import PanelHeader, {
    PanelHeaderButton,
} from '~/components/toolbar/shared/PanelHeader';
import { useToolRailSlot } from '~/components/toolbar/shared/ToolRailSlots';
import {
    DOCKED_INSET_PX,
    type DockPlacement,
    dockPlacement,
} from '~/components/toolbar/shared/toolOptionsDockPlacement';
import { ToolOptionsDockContext } from '~/components/toolbar/shared/toolOptionsDockContext';
import { tooltipsBesidePanel } from '~/components/toolbar/shared/tooltipsBesidePanel';
import {
    useKeptInWindow,
    usePanelDrag,
} from '~/components/toolbar/shared/usePanelDrag';
import {
    shownOperationDocksState,
    toolOptionsDockState,
} from '~/state/atoms/toolsAtoms';
import { toolSurfaceSx } from '~/theme/theme';

/** Width of a tool's open dock, in CSS pixels. */
const TOOL_DOCK_WIDTH_PX = 256;

/**
 * What the dock holds: a tool's options, which fold to a tab and
 * give way to an open operation, or an operation's, which close with the
 * operation.
 */
type ToolOptionsDockKind = 'tool' | 'operation';

interface ToolOptionsDockProps {
    kind: ToolOptionsDockKind;
    /**
     * Names what the dock holds. A new title opens a folded or collapsed
     * tool dock again, as choosing another tool does.
     */
    title: string;
    /** Shown in a tinted tile before the title. */
    icon?: ReactNode;
    /** Width of the open dock, in CSS pixels. A tool's dock is 256 px. */
    width?: number;
    /**
     * The `dockAnchor` of the button the dock lines up with, in the rail
     * group that `kind` names. By default it is the group's highlighted
     * button.
     */
    anchor?: string;
    /** Closes an operation. A tool's dock folds to a tab instead. */
    onClose?: () => void;
    /** Refuses the close while an operation is writing. */
    closeDisabled?: boolean;
    /**
     * Whether a press on a view collapses the docked dock to one line. By
     * default a tool's dock does and an operation's does not. A dock whose
     * options the user acts on next, after working on a view, stays open.
     */
    collapsesOnViewPress?: boolean;
    /**
     * The values in force, shown after the title while the dock is
     * collapsed, so they can be read without opening it. An element that
     * reads them itself re-renders alone as they change; one that renders
     * nothing leaves no gap.
     */
    summary?: ReactNode;
    /**
     * Rows under the options that stay in view as the options scroll. They
     * mount only while the dock is open.
     */
    footer?: ReactNode;
    /** The options. */
    children: ReactNode;
}

/**
 * A floating panel over the views that shows the options of the active
 * tool or operation. It renders into the dock layer and does nothing
 * without one. The options it holds stay mounted through every state below, so
 * their values, previews and effects carry on while they are out of sight.
 * Its footer rows mount only while it is open.
 *
 * - Docked, it sits beside the tool rail, level with the active button.
 * - A press on a view collapses a docked tool's dock to a one-line strip,
 *   until the pointer or the focus comes back to it, or the strip is
 *   clicked. An operation's dock stays open, as its preview does, and so
 *   does a dock that opts out (`collapsesOnViewPress`).
 * - Its close button folds a tool's options to a tab beside the rail, and
 *   a click on the tab unfolds them. An operation's close button closes the
 *   operation, so no operation preview stays on the views with its options
 *   out of sight.
 * - Dragged by its title, or moved by the arrow keys while its title has
 *   the focus (Shift for larger steps), it detaches and stays where it is
 *   dropped, across tools, anywhere in the window and kept inside it as the
 *   window and the dock resize, until Dock returns it to the rail. A
 *   detached dock always shows its options: it collapses and folds only
 *   beside the rail, so folding one returns it there.
 * - A tool's dock gives way while an operation's dock shows.
 */
export default function ToolOptionsDock({
    kind,
    title,
    icon,
    width,
    anchor,
    onClose,
    closeDisabled = false,
    collapsesOnViewPress = kind === 'tool',
    summary,
    footer,
    children,
}: ToolOptionsDockProps) {
    const intl = useIntl();
    const summaryId = useId();
    const host = useToolRailSlot('panels');
    const railSlot = useToolRailSlot(kind === 'tool' ? 'tools' : 'ops');
    const [dock, setDock] = useRecoilState(toolOptionsDockState);
    const [shownOperationDocks, setShownOperationDocks] = useRecoilState(
        shownOperationDocksState
    );
    const [placement, setPlacement] = useState<DockPlacement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);
    // The state whose control takes the focus once the dock shows it:
    // the control that held the focus unmounts with its own state. In
    // 'open' the title takes it, otherwise the strip.
    const focusOn = useRef<'open' | 'tab' | null>(null);

    const detached = dock.detachedPosition !== null;
    const state: 'open' | 'thin' | 'tab' =
        kind === 'tool' && dock.mode === 'folded' && !detached
            ? 'tab'
            : dock.mode === 'thin' && collapsesOnViewPress && !detached
              ? 'thin'
              : 'open';
    const givesWay = kind === 'tool' && shownOperationDocks > 0;
    const size = kind === 'tool' ? 'small' : 'medium';
    const openWidth =
        width ?? (kind === 'tool' ? TOOL_DOCK_WIDTH_PX : undefined);

    const moveTo = useCallback(
        (position: PanelPosition) =>
            setDock((current) => ({
                ...current,
                mode: 'open',
                detachedPosition: position,
            })),
        [setDock]
    );
    const dragListeners = usePanelDrag(panelRef, moveTo);
    // A tool's explanations open beside its options, not over them, and
    // what the options hold can find the dock that holds them.
    const theme = useTheme();
    const optionsTheme = useMemo(
        () => tooltipsBesidePanel(theme, panelRef),
        [theme]
    );
    const besideOptions = (content: ReactNode) => (
        <ToolOptionsDockContext.Provider value={panelRef}>
            {kind === 'tool' ? (
                <ThemeProvider theme={optionsTheme}>{content}</ThemeProvider>
            ) : (
                content
            )}
        </ToolOptionsDockContext.Provider>
    );
    useKeptInWindow(panelRef, host ? dock.detachedPosition : null, moveTo);

    useEffect(() => {
        if (kind !== 'operation' || !host) {
            return undefined;
        }
        setShownOperationDocks((count) => count + 1);
        return () => setShownOperationDocks((count) => count - 1);
    }, [host, kind, setShownOperationDocks]);

    // A tool's dock opens as it mounts and when its title changes, as
    // choosing another tool does.
    useLayoutEffect(() => {
        if (kind === 'tool') {
            setDock((current) =>
                current.mode === 'open' ? current : { ...current, mode: 'open' }
            );
        }
    }, [title, kind, setDock]);

    const focusTitle = () =>
        panelRef.current
            ?.querySelector<HTMLElement>('[data-drag-handle]')
            ?.focus();

    useLayoutEffect(() => {
        if (state !== focusOn.current) {
            return;
        }
        focusOn.current = null;
        if (state === 'open') {
            focusTitle();
        } else {
            panelRef.current?.querySelector<HTMLElement>('button')?.focus();
        }
    }, [state]);

    // Lines the docked dock up with its button again as the host or the
    // dock resize, as the rail scrolls, and as the rail's buttons come,
    // go or change highlight. Equal results keep state.
    useLayoutEffect(() => {
        if (!host) {
            return undefined;
        }
        const measure = () => {
            const button =
                railSlot?.querySelector<HTMLElement>(
                    anchor === undefined
                        ? '[data-highlighted]'
                        : `[data-dock-anchor="${anchor}"]`
                ) ?? null;
            const next = dockPlacement(
                host.getBoundingClientRect(),
                button?.getBoundingClientRect() ?? null
            );
            setPlacement((current) =>
                current &&
                current.top === next.top &&
                current.bottom === next.bottom &&
                current.maxHeight === next.maxHeight
                    ? current
                    : next
            );
        };
        measure();
        const resizes = new ResizeObserver(measure);
        resizes.observe(host);
        if (panelRef.current) {
            resizes.observe(panelRef.current);
        }
        const buttons = new MutationObserver(measure);
        if (railSlot) {
            buttons.observe(railSlot, {
                subtree: true,
                childList: true,
                attributes: true,
                attributeFilter: ['data-highlighted'],
            });
        }
        const rail = railSlot?.parentElement;
        rail?.addEventListener('scroll', measure);
        return () => {
            resizes.disconnect();
            buttons.disconnect();
            rail?.removeEventListener('scroll', measure);
        };
    }, [anchor, host, railSlot]);

    const unfold = () => setDock((current) => ({ ...current, mode: 'open' }));

    const handleClose = () => {
        if (kind === 'operation') {
            onClose?.();
            return;
        }
        focusOn.current = 'tab';
        setDock((current) => ({
            ...current,
            mode: 'folded',
            detachedPosition: null,
        }));
    };

    if (!host) {
        return null;
    }

    // A detached dock is placed in the window, so it can leave the
    // views for any part of it.
    const position = dock.detachedPosition
        ? {
              position: 'fixed' as const,
              left: dock.detachedPosition.x,
              top: dock.detachedPosition.y,
              maxHeight: `calc(100vh - ${2 * PANEL_WINDOW_INSET_PX}px)`,
          }
        : {
              position: 'absolute' as const,
              left: DOCKED_INSET_PX,
              top: placement?.top,
              bottom: placement?.bottom,
              maxHeight: placement?.maxHeight,
          };

    return createPortal(
        <PanelFrame
            ref={panelRef}
            size={size}
            footer={besideOptions(footer)}
            // Not a dialog: the views and their shortcuts stay in use
            // while it shows.
            role="group"
            aria-label={title}
            data-testid="tool-options-dock"
            data-dock-state={state}
            onMouseEnter={(event) => {
                // A stroke dragged across the strip leaves it as it is.
                if (state === 'thin' && event.buttons === 0) {
                    unfold();
                }
            }}
            onFocus={() => {
                if (state === 'thin') {
                    focusOn.current = 'open';
                    unfold();
                }
            }}
            sx={[
                kind === 'tool' && toolSurfaceSx,
                (theme) => ({
                    ...position,
                    display: givesWay ? 'none' : 'flex',
                    width:
                        state === 'open' && openWidth !== undefined
                            ? openWidth
                            : 'max-content',
                    // A detached dock keeps its own size, so it moves back
                    // inside a smaller window.
                    maxWidth: dock.detachedPosition
                        ? `calc(100vw - ${2 * PANEL_WINDOW_INSET_PX}px)`
                        : `calc(100% - ${2 * DOCKED_INSET_PX}px)`,
                    ...(state !== 'open' && theme.mixins.glassOverlay),
                    borderColor: detached ? 'primary.main' : 'divider',
                    '&:hover':
                        state === 'open'
                            ? undefined
                            : { borderColor: 'primary.main' },
                }),
            ]}
            header={
                <PanelHeader
                    title={title}
                    icon={icon}
                    dragListeners={dragListeners}
                    size={size}
                    onClose={handleClose}
                    closeDisabled={closeDisabled}
                    closeLabel={
                        kind === 'tool'
                            ? intl.formatMessage({
                                  id: 'toolOptions.close',
                              })
                            : undefined
                    }
                    actions={
                        detached && (
                            <PanelHeaderButton
                                label={intl.formatMessage({
                                    id: 'toolOptions.dock',
                                })}
                                size={size}
                                onClick={(event) => {
                                    // Only a keyboard press moves the
                                    // focus to the title.
                                    if (event.detail === 0) {
                                        focusTitle();
                                    }
                                    setDock((current) => ({
                                        ...current,
                                        detachedPosition: null,
                                    }));
                                }}
                            >
                                <PanelLeft size={16} />
                            </PanelHeaderButton>
                        )
                    }
                />
            }
            collapsed={
                state === 'open' ? undefined : (
                    <Tooltip
                        title={intl.formatMessage({
                            id: 'toolOptions.show',
                        })}
                        placement="right"
                    >
                        <ButtonBase
                            // A mouse press does not focus the strip, so
                            // the dock opens with the focus where it was.
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={(event) => {
                                if (event.detail === 0) {
                                    focusOn.current = 'open';
                                }
                                unfold();
                            }}
                            aria-label={intl.formatMessage({
                                id: 'toolOptions.show',
                            })}
                            // The label replaces the content as the name, so
                            // the values in force are read as its description.
                            aria-describedby={summary ? summaryId : undefined}
                            sx={{
                                height: 26,
                                px: 1.5,
                                gap: 1,
                                justifyContent: 'flex-start',
                            }}
                        >
                            <Box
                                aria-hidden
                                sx={{
                                    width: 3,
                                    height: 14,
                                    borderRadius: '1px',
                                    bgcolor: 'primary.main',
                                }}
                            />
                            <Typography
                                noWrap
                                sx={{ fontSize: 11, fontWeight: 500 }}
                            >
                                {title}
                            </Typography>
                            {summary && (
                                <Typography
                                    id={summaryId}
                                    variant="monoMeta"
                                    sx={{ '&:empty': { display: 'none' } }}
                                >
                                    {summary}
                                </Typography>
                            )}
                        </ButtonBase>
                    </Tooltip>
                )
            }
        >
            {besideOptions(children)}
        </PanelFrame>,
        host
    );
}
